import Notifications from '@components/Notifications/Notifications';
import { SideNav } from '@components/SideNav2/SideNav';
import { IndividualUserUnspportedBreakpointOverlay } from '@components/UnsupportedBreakpointOverlays/IndividualUserOverlay';
import { TeamAdminUnsupportedBreakpointOverlay } from '@components/UnsupportedBreakpointOverlays/TeamAdminOverlay';
import { upgradeFlowContext } from '@context/upgrade-flow-context';
import { makePageTitle } from '@modules/page-title';
import { IntercomMessenger } from '@modules/scripts/intercom';
import { useUserProfile } from '@modules/user-profile/UserProfileProvider';
import { getUserProfileFullName } from '@modules/user-profile/user-profile-helpers';
import { useUnifiedProfileFlags } from 'hooks/use-unified-profile-flags';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { FC, ReactNode } from 'react';

import styles from './Layout.module.scss';

interface Layout2Props {
  title?: string;
  children: ReactNode;
  useLegacyHomeBreakpoint?: boolean;
}

const Layout2: FC<Layout2Props> = ({
  children,
  title = 'Blinq',
  useLegacyHomeBreakpoint = false,
}) => {
  const { userProfileDetails, helpers } = useUserProfile();
  const { currentWorkspace } = userProfileDetails;
  const isTeamAdmin = helpers.isAtLeastTeamAdmin;
  const router = useRouter();
  const isFromOnboarding = router.query.from_onboarding === 'true';

  const pageTitle = makePageTitle(title);

  // Same rule as the App Router side nav (app/[workspace]/layout.tsx): the
  // /dash home rollout covers individual owners under phase 1 and team
  // members under phase 2. Team admins have their own home (/home).
  const { phase1, phase2 } = useUnifiedProfileFlags();

  const showDashHome =
    currentWorkspace.type !== 'TEAM'
      ? currentWorkspace.profile.role === 'OWNER' && phase1 === 'on'
      : currentWorkspace.profile.role === 'MEMBER' && phase2 === 'on';

  return (
    <upgradeFlowContext.Provider value={{ flowType: 'in-app' }}>
      <IntercomMessenger
        userId={userProfileDetails.id}
        name={getUserProfileFullName(userProfileDetails)}
        email={userProfileDetails.email ?? undefined}
        workspaceType={currentWorkspace.type}
      />
      <div className={styles.layout}>
        <Head>
          <title>{pageTitle}</title>
        </Head>
        {/* Unsupported Breakpoint Overlays */}
        {isTeamAdmin ? (
          <TeamAdminUnsupportedBreakpointOverlay
            useLegacyHomeBreakpoint={useLegacyHomeBreakpoint}
            isFromOnboarding={isFromOnboarding}
          />
        ) : (
          <IndividualUserUnspportedBreakpointOverlay />
        )}
        <SideNav
          workspaceId={currentWorkspace.id}
          workspaceName={currentWorkspace.name ?? ''}
          workspaceRole={currentWorkspace.profile.role}
          workspaceType={currentWorkspace.type}
          workspaceAvatarUrl={currentWorkspace.avatarUrl ?? undefined}
          planType={currentWorkspace.plan.type}
          planDescription={currentWorkspace.plan.name}
          showDashHome={showDashHome}
        />
        <div className={styles.contents}>
          <main className={styles['main-content']}>{children}</main>
          <div className={styles['notifications-section']}>
            <Notifications />
          </div>
        </div>
      </div>
    </upgradeFlowContext.Provider>
  );
};

export default Layout2;
